"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMissingDependentValuesets = exports.parseTimeStringAsUTCConvertingToEndOfYear = exports.parseTimeStringAsUTC = exports.valueSetsForCodeService = void 0;
const moment_1 = __importDefault(require("moment"));
const CustomErrors_1 = require("../types/errors/CustomErrors");
/**
 * Create the code service valueset database that the cql-execution engine needs.
 *
 * NOTE: This uses the `compose` attribue of the ValueSet to get code. This is incorrect and
 * should be using the `expansion`. But current example measures have ValueSets with compose
 * only.
 *
 * @param valueSetResources FHIR ValueSets.
 * @returns The value set DB structure needed for the cql-execution CodeService.
 */
function valueSetsForCodeService(valueSetResources) {
    const valueSets = {};
    let valueSetId;
    let version;
    valueSetResources.forEach(valueSet => {
        if (valueSet.url) {
            // Grab id for this valueset (should match FHIR ValueSet url)
            valueSetId = valueSet.url;
            if (!valueSets[valueSetId]) {
                valueSets[valueSetId] = {};
            }
            // Grab ValueSet version. This usually is not used.
            version = valueSet.version || '';
            if (version === 'N/A') {
                version = '';
            }
            // Create array for valueset members.
            if (!valueSets[valueSetId][version]) {
                valueSets[valueSetId][version] = [];
            }
        }
        else {
            // TODO: handle situation where ValueSet does not have URL
        }
        if (valueSet.expansion && valueSet.expansion.contains && valueSet.expansion.contains.length > 0) {
            // Default to using expansion if it exists
            valueSets[valueSetId][version] = getHierarchicalCodes(valueSet.expansion.contains);
        }
        else if (valueSet.compose) {
            // Only use compose if expansion doesn't exist
            // Iterate over include components and add all concepts
            valueSet.compose.include.forEach(include => {
                var _a;
                (_a = include.concept) === null || _a === void 0 ? void 0 : _a.forEach(concept => {
                    if (concept.code && include.system) {
                        valueSets[valueSetId][version].push({
                            code: concept.code,
                            system: include.system,
                            version: include.version,
                            display: concept.display
                        });
                    }
                });
            });
        }
        else {
            // TODO: Handle situation when ValueSet does not have expansion or compose.
        }
    });
    return valueSets;
}
exports.valueSetsForCodeService = valueSetsForCodeService;
function getHierarchicalCodes(contains) {
    const codes = [];
    contains.forEach(contain => {
        if (!contain.abstract && !contain.inactive && contain.code && contain.system) {
            codes.push({
                code: contain.code,
                system: contain.system,
                version: contain.version,
                display: contain.display
            });
        }
        if (contain.contains && contain.contains.length > 0) {
            codes.push(...getHierarchicalCodes(contain.contains));
        }
    });
    return codes;
}
// Create Date from UTC string date and time using momentJS
function parseTimeStringAsUTC(timeValue) {
    return moment_1.default.utc(timeValue, moment_1.default.defaultFormatUtc).toDate();
}
exports.parseTimeStringAsUTC = parseTimeStringAsUTC;
// Create Date from UTC string date and time using momentJS, shifting to 11:59:59 of the given year
function parseTimeStringAsUTCConvertingToEndOfYear(timeValue) {
    return moment_1.default.utc(timeValue, moment_1.default.defaultFormatUtc).add(1, 'years').subtract(1, 'seconds').toDate();
}
exports.parseTimeStringAsUTCConvertingToEndOfYear = parseTimeStringAsUTCConvertingToEndOfYear;
/**
 * Collates dependent valuesets from a measure by going through all of the valuesets listed in the relatedArtifacts of the measure bundle's libraries,
 * as well as the libraries dataCriteria's codeFilters, then finds all valuesets that are not already contained in the measure bundle.
 *
 * @param {fhir4.Bundle} measureBundle - A measure bundle object that contains all libraries and valuesets used by the measure
 * @param {fhir4.ValueSet[]} valueSetCache - Cache of existing valueset objects on disk to include in lookup
 * @returns {string[]} An array of all dependent valueset urls in the measure that are used by the measure's libraries but not contained in the measure bundle
 */
function getMissingDependentValuesets(measureBundle, valueSetCache = []) {
    var _a;
    if (!measureBundle.entry) {
        throw new CustomErrors_1.UnexpectedResource('Expected measure bundle to contain entries');
    }
    const libraryEntries = (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.filter(e => { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'Library' && (e.resource.dataRequirement || e.resource.relatedArtifact); });
    // create an array of valueset urls
    const vsUrls = libraryEntries.reduce((acc, lib) => {
        const libraryResource = lib.resource;
        if (!libraryResource) {
            throw new CustomErrors_1.UnexpectedResource('Library entry not included in measure bundle');
        }
        else if (!libraryResource.relatedArtifact &&
            !(libraryResource.dataRequirement && libraryResource.dataRequirement.length > 0)) {
            throw new CustomErrors_1.UnexpectedProperty('Expected Library entry to have resource with relatedArtifacts or dataRequirements that have codeFilters');
        }
        // pull all valueset urls out of this library's dataRequirements
        const libraryVsUrls = [];
        if (libraryResource.dataRequirement) {
            libraryVsUrls.push(...libraryResource.dataRequirement.reduce((accumulator, dr) => {
                if (dr.codeFilter && dr.codeFilter.length > 0) {
                    // get each valueset url for each codeFilter (if valueset url exists)
                    const vs = dr.codeFilter
                        .filter(cf => cf.valueSet)
                        .map(cf => {
                        return cf.valueSet;
                    });
                    return accumulator.concat(vs);
                }
                else {
                    return accumulator;
                }
            }, []));
        }
        if (libraryResource.relatedArtifact) {
            libraryVsUrls.push(...libraryResource.relatedArtifact.reduce((accumulator, ra) => {
                if (ra.type === 'depends-on') {
                    // look in invalid `url` field. may be needed for older measures
                    if (ra.url && ra.url.includes('ValueSet')) {
                        accumulator.push(ra.url);
                        // look in `resource` field
                    }
                    else if (ra.resource && ra.resource.includes('ValueSet')) {
                        accumulator.push(ra.resource);
                    }
                }
                return accumulator;
            }, []));
        }
        return acc.concat(libraryVsUrls);
    }, []);
    // unique-ify
    const uniqueVS = vsUrls.filter((value, index, self) => self.indexOf(value) === index);
    // full array of all valueset URLs present across measureBundle and cache
    const existingValueSets = measureBundle.entry
        .filter(e => { var _a; return ((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'ValueSet'; })
        .map(e => e.resource)
        .concat(valueSetCache)
        .map(vs => vs.url);
    // filter to any valueset urls that cannot be found
    return uniqueVS.filter(url => {
        return !existingValueSets.includes(url);
    });
}
exports.getMissingDependentValuesets = getMissingDependentValuesets;
//# sourceMappingURL=ValueSetHelper.js.map